import { ChevronLeft, Mail, MessageCircle, Clock, Send } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';

export default function AppContactSupport() {
    const navigate = useNavigate();
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [category, setCategory] = useState('general');
    const [submitted, setSubmitted] = useState(false);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        // Open email client with pre-filled content
        const emailSubject = encodeURIComponent(`[${category.toUpperCase()}] ${subject}`);
        const emailBody = encodeURIComponent(message);
        window.location.href = `mailto:support@way2will.com?subject=${emailSubject}&body=${emailBody}`;

        setSubmitted(true);
    };

    if (submitted) {
        return (
            <div className="px-4 pb-8">
                <div className="flex items-center gap-3 mb-6">
                    <button
                        onClick={() => navigate(-1)}
                        className="w-10 h-10 flex items-center justify-center rounded-full bg-[#F5F8FA] dark:bg-dark-elevated"
                    >
                        <ChevronLeft className="w-5 h-5 text-[#4A5C6A]" />
                    </button>
                    <h1 className="text-xl font-bold text-[#1A2832] dark:text-white">Contact Support</h1>
                </div>

                <div className="bg-[#D5F2EA] rounded-2xl p-6 text-center">
                    <div className="w-16 h-16 bg-[#3DA58A] rounded-full flex items-center justify-center mx-auto mb-4">
                        <Send className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-xl font-bold text-[#1A2832] mb-2">Email Client Opened!</h2>
                    <p className="text-[#4A5C6A] mb-4">
                        Complete sending your message in your email app. We typically respond within 24 hours.
                    </p>
                    <button
                        onClick={() => {
                            setSubmitted(false);
                            setSubject('');
                            setMessage('');
                        }}
                        className="px-6 py-3 bg-[#0D5F6B] text-white rounded-xl font-semibold"
                    >
                        Send Another Message
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="px-4 pb-8">
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
                <button
                    onClick={() => navigate(-1)}
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-[#F5F8FA] dark:bg-dark-elevated"
                >
                    <ChevronLeft className="w-5 h-5 text-[#4A5C6A]" />
                </button>
                <h1 className="text-xl font-bold text-[#1A2832] dark:text-white">Contact Support</h1>
            </div>

            {/* Response Time Banner */}
            <div className="bg-[#D9EEF2] rounded-xl p-4 mb-6 flex items-center gap-3">
                <Clock className="w-5 h-5 text-[#0D5F6B]" />
                <div>
                    <p className="text-sm font-medium text-[#0D5F6B]">Average response time: 24 hours</p>
                    <p className="text-xs text-[#4A5C6A]">We're here to help!</p>
                </div>
            </div>

            {/* Direct Email */}
            <div className="bg-white dark:bg-dark-card rounded-2xl p-4 mb-6 shadow-sm">
                <div className="flex items-center gap-3 mb-2">
                    <Mail className="w-5 h-5 text-[#0D5F6B]" />
                    <span className="font-semibold text-[#1A2832] dark:text-white">Email Us Directly</span>
                </div>
                <a
                    href="mailto:support@way2will.com"
                    className="text-[#0D5F6B] font-medium text-lg hover:underline"
                >
                    support@way2will.com
                </a>
            </div>

            {/* Contact Form */}
            <div className="bg-white dark:bg-dark-card rounded-2xl p-4 shadow-sm">
                <h2 className="text-base font-semibold text-[#1A2832] dark:text-white mb-4">
                    <MessageCircle className="w-5 h-5 inline mr-2 text-[#0D5F6B]" />
                    Send a Message
                </h2>

                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Category */}
                    <div>
                        <label className="block text-sm font-medium text-[#1A2832] dark:text-white mb-2">
                            Category
                        </label>
                        <select
                            value={category}
                            onChange={(e) => setCategory(e.target.value)}
                            className="w-full px-4 py-3 rounded-xl border border-[#DCE4EA] dark:border-dark-border bg-white dark:bg-dark-elevated text-[#1A2832] dark:text-white focus:outline-none focus:border-[#0D5F6B]"
                        >
                            <option value="general">General Inquiry</option>
                            <option value="bug">Bug Report</option>
                            <option value="billing">Billing Question</option>
                            <option value="integration">Integration Help</option>
                            <option value="account">Account Issue</option>
                        </select>
                    </div>

                    {/* Subject */}
                    <div>
                        <label className="block text-sm font-medium text-[#1A2832] dark:text-white mb-2">
                            Subject
                        </label>
                        <input
                            type="text"
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            placeholder="Brief description of your issue"
                            required
                            className="w-full px-4 py-3 rounded-xl border border-[#DCE4EA] dark:border-dark-border bg-white dark:bg-dark-elevated text-[#1A2832] dark:text-white placeholder-[#7B8C9B] focus:outline-none focus:border-[#0D5F6B]"
                        />
                    </div>

                    {/* Message */}
                    <div>
                        <label className="block text-sm font-medium text-[#1A2832] dark:text-white mb-2">
                            Message
                        </label>
                        <textarea
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder="Describe your issue in detail..."
                            required
                            rows={5}
                            className="w-full px-4 py-3 rounded-xl border border-[#DCE4EA] dark:border-dark-border bg-white dark:bg-dark-elevated text-[#1A2832] dark:text-white placeholder-[#7B8C9B] focus:outline-none focus:border-[#0D5F6B] resize-none"
                        />
                    </div>

                    {/* Submit */}
                    <button
                        type="submit"
                        className="w-full py-4 bg-[#0D5F6B] text-white rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-[#0A4E59] transition-colors"
                    >
                        <Send className="w-5 h-5" />
                        Send Message
                    </button>
                </form>
            </div>
        </div>
    );
}
